import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import type { SherwinWilliamsColor } from '@/types/firestore';

interface DeleteColorConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  color: SherwinWilliamsColor | null;
  onConfirm: () => Promise<void>;
  isDeleting?: boolean;
  isFeatured?: boolean;
}

export function DeleteColorConfirmDialog({
  open,
  onOpenChange,
  color,
  onConfirm,
  isDeleting = false,
  isFeatured = false,
}: DeleteColorConfirmDialogProps) {
  if (!color) return null;

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Custom Color</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="flex items-center gap-3 pt-2">
                <div
                  className="h-10 w-10 rounded-md border-2 border-border shadow-sm"
                  style={{ backgroundColor: color.hex }}
                  aria-label={`Color swatch for ${color.name}`}
                />
                <div>
                  <div className="font-medium text-foreground">{color.name}</div>
                  <div className="text-xs text-muted-foreground">{color.code}</div>
                </div>
              </div>
              <p>
                Are you sure you want to delete this custom color? This action cannot
                be undone.
              </p>
              {isFeatured && (
                <p className="text-amber-600 dark:text-amber-500 font-medium">
                  This color is currently featured in your widget and will be removed
                  from the featured list.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete Color
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
